import { XYZ } from 'ol/source';
import { DATABASE_VERSION_PARAM, LIGHT_CONDITION_PARAM, DEFAULT_LIGHT_CONDITION } from './GeomodSourceUtils';
import { TileLayerCacheStrategy } from '../utils/cache';
import { LAYERS_PARAMETERS_UPDATED_EVENT_TYPE } from '../utils/events.js';
// import { updateQueryString, tileLoader } from '../utils/httpRequest';
import { updateQueryString } from '../utils/httpRequest';

/**
 * @typedef {SpecificGeomodTMSOptions & import('ol/source/XYZ').Options} GeomodTMSOptions
 */

/**
 * @typedef {Object} SpecificGeomodTMSOptions
 * @property {string} name The layer name.
 * @property {string} [color] The layer color (DAY, DUSK, NIGHT).
 * @property {TileLayerCacheStrategyOptions} [cache]  Cache strategy settings.
 * @property {Map<string,string>} [extraParameters] The extra parameters for the request exemple: AIS_SHOW_TRACE=false.
 */

/**
 * The database version regexp to change the TMS version.
 * @type {RegExp}
 */
const TMS_DATABASE_VERSION_REGEXP = /\{v\}/g;

/**
 * The layer name regexp to change the TMS name (toto_DAY to toto_DUSK for example).
 * @type {RegExp}
 */
const TMS_NAME_REGEXP = /\{name\}/g;

/**
 * The Geomod WMS tile source.
 * @extends {XYZ}
 */
export default class GeomodTMS extends XYZ {
  /**
     * @constructor
     * @param {GeomodTMSOptions} options The options.
     */
  constructor(options = {}) {
    let extraParametersOptions;
    if (options.extraParameters) {
      extraParametersOptions = {};
      const extraParameters = JSON.parse(localStorage.getItem('layerExtraParameters')) || {};
      for (const key of options.extraParameters) {
        extraParametersOptions[key] = extraParameters[key];
      }
    }

    // options.tileLoadFunction = tileLoader;
    options.interpolate = false
    super(options);

    /**
         * The layer extra parameters.
         * @type {Object<string,string>}
         */
    this.params_ = {
      ...extraParametersOptions,
      [LIGHT_CONDITION_PARAM]: options.color || DEFAULT_LIGHT_CONDITION,
      token: localStorage.getItem('token')
    };

    /**
         * The original urls
         * @type {Array<string>}
         */
    this.originalUrls_ = this.getUrls();
    /**
         * Le layer name.
         * @private
         * @type {string}
         */
    this.name_ = options.name;

    /**
         * The source tag
         * @private
         * @type {string}
         */
    this.tag_ = options.filter;

    /**
         * The cache strategy used by the layer
         * @type {TileLayerCacheStrategy}
         */
    this.cacheStrategy = new TileLayerCacheStrategy(options.cache);
    document.addEventListener(LAYERS_PARAMETERS_UPDATED_EVENT_TYPE, () => {
      this.cacheStrategy.run(this);
    });
  }

  /**
     * Get the source tag.
     * @return {string} The source tag.
     */
  getTag() {
    return this.tag_;
  }

  /**
     * Get the layer name.
     * @returns {string} The layer name.
     */
  getName() {
    return this.name_;
  }

  /**
     * Get the user-provided params, i.e. those passed to the constructor through
     * the "params" option, and possibly updated using the updateParams method.
     * @return {Object<string,any>} Params.
     */
  getParams() {
    return this.params_;
  }

  /**
     * Update the user-provided params.
     * @param {Object} params Params.
     */
  updateParams(params) {
    this.params_ = { ...this.params_, ...params };
    const query = { ...this.params_ };
    const color = query[LIGHT_CONDITION_PARAM];
    delete query[LIGHT_CONDITION_PARAM];
    const dbVersion = query[DATABASE_VERSION_PARAM];
    delete query[DATABASE_VERSION_PARAM];

    const urls = [...this.originalUrls_];
    let layerName = this.name_;
    if (this.params_[LIGHT_CONDITION_PARAM]) {
      layerName += `_${color}`;
    }
    for (let i = 0; i < urls.length; i++) {
      urls[i] = urls[i].replace(TMS_DATABASE_VERSION_REGEXP, dbVersion).replace(TMS_NAME_REGEXP, layerName);
    }
    this.setUrls(urls.map((url) => updateQueryString(url, query)));
    this.refresh();
  }
}
